import { useCallback, useEffect, useState } from "react";
import { Permissions } from "@src/domain/permissions/Permissions";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { GraphQlAdapter } from "@src/infrastructure/adapters/graphQl/GraphQl.adapter";
import { UpdateBrandUserStatusDTO } from "@src/domain/dto/brands";
import { UpdateBrandUserStatusUseCase } from "@src/application/useCases/brand";
import { BrandRepository } from "@src/infrastructure/repositories/brand";
import { useOrganizationsData } from "@src/presentation/providers/OrganizationsDataProvider";
import { BrandUser } from "@src/domain/entities/brand";
import { Flex, Icon, Icons, Spin, Switch, useToast } from "@ir/ui";

export interface BrandUserStatusUpdateProps {
  brandId: number;
  brandUserId: number;
  brandUserIsActive: boolean;
  rowBrandUser?: BrandUser;
  onUserStatusUpdated?: (user: BrandUser) => void;
}

export const BrandUserStatusUpdate = ({
  brandId,
  brandUserId,
  brandUserIsActive,
  rowBrandUser,
  onUserStatusUpdated,
}: BrandUserStatusUpdateProps) => {
  const { applyBrandUserUpdate, organizationDetails } = useOrganizationsData();
  const [pendingActive, setPendingActive] = useState(brandUserIsActive);
  const [isUpdating, setIsUpdating] = useState(false);
  const { error: errorToast, success: successToast } = useToast();

  useEffect(() => {
    setPendingActive(brandUserIsActive);
  }, [brandUserIsActive]);

  const handleSwitchChange = useCallback(
    async (mode: boolean) => {
      setPendingActive(mode);
      setIsUpdating(true);

      try {
        const currentUser =
          organizationDetails?.brands
            ?.find((brand) => brand.brandId === brandId)
            ?.brandUsers?.find((user) => user.brandUserId === brandUserId) ??
          rowBrandUser ??
          null;

        if (currentUser) {
          const optimisticUser = new BrandUser({
            brandUserId: currentUser.brandUserId,
            brandUserName: currentUser.brandUserName,
            brandUserEmail: currentUser.brandUserEmail,
            brandUserRoles: currentUser.brandUserRoles,
            brandAppUserId: currentUser.brandAppUserId,
            brandUserIsActive: mode,
            brandUserCreatedAt: currentUser.brandUserCreatedAt,
            brandUserCreatedBy: currentUser.brandUserCreatedBy,
            brandOrganizationUserId: currentUser.brandOrganizationUserId,
          });
          applyBrandUserUpdate(brandId, optimisticUser);
        }

        const payload = { brandId, brandUserId, isActive: mode };
        const validated = UpdateBrandUserStatusDTO.validate(payload);
        if (!validated.success) {
          console.error(
            "UpdateBrandUserStatus validation failed",
            validated.error,
          );
          throw new Error("UpdateBrandUserStatus validation failed");
        }

        const dto = new UpdateBrandUserStatusDTO(validated.data);
        const updatedUser = await new UpdateBrandUserStatusUseCase(
          new BrandRepository(GraphQlAdapter.getInstance().getClient()),
        ).execute(dto);

        applyBrandUserUpdate(brandId, updatedUser);
        onUserStatusUpdated?.(updatedUser);

        successToast({
          content: "User status updated",
          icon: <Icon name={Icons.CheckCircle} />,
        });
      } catch (error) {
        console.error("Failed to update brand user status", error);
        setPendingActive(brandUserIsActive);

        const currentUser =
          organizationDetails?.brands
            ?.find((brand) => brand.brandId === brandId)
            ?.brandUsers?.find((user) => user.brandUserId === brandUserId) ??
          rowBrandUser ??
          null;

        if (currentUser) {
          const revertedUser = new BrandUser({
            brandUserId: currentUser.brandUserId,
            brandUserName: currentUser.brandUserName,
            brandUserEmail: currentUser.brandUserEmail,
            brandUserRoles: currentUser.brandUserRoles,
            brandAppUserId: currentUser.brandAppUserId,
            brandUserIsActive,
            brandUserCreatedAt: currentUser.brandUserCreatedAt,
            brandUserCreatedBy: currentUser.brandUserCreatedBy,
            brandOrganizationUserId: currentUser.brandOrganizationUserId,
          });
          applyBrandUserUpdate(brandId, revertedUser);
          onUserStatusUpdated?.(revertedUser);
        }

        errorToast({
          content: "Failed to update user status",
          icon: <Icon name={Icons.AlertCircle} />,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [
      applyBrandUserUpdate,
      brandId,
      brandUserId,
      brandUserIsActive,
      organizationDetails,
      successToast,
      errorToast,
      rowBrandUser,
      onUserStatusUpdated,
    ],
  );

  return (
    <HasAnyPermission requiredPermissions={[Permissions.BRANDS_EDIT]}>
      <Flex align="center" gap={0.5}>
        {isUpdating && <Spin size="small" spinning />}
        <Switch
          size="small"
          isSelected={pendingActive}
          onChange={handleSwitchChange}
          isDisabled={isUpdating}
          aria-label="Brand user status update"
          role="switch"
          htmlTitle="Update user status"
        />
      </Flex>
    </HasAnyPermission>
  );
};
